import React, { useState, useEffect } from 'react';
import { Search, X, ChefHat, Clock, Users, Sparkles, BookOpen } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import useSubscription from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TribalTable = () => {
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const { toast } = useToast();
  const { user } = useAuth();
  const { isPaidUser, hasFeatureAccess } = useSubscription();

  // Check if user is admin
  const isAdmin = user && (
    (user.email && user.email.toLowerCase().includes('admin')) || 
    (user.role && user.role.toLowerCase() === 'admin') ||
    (user.is_admin === true)
  );

  // Allow access for admin users or paid subscribers
  const hasRecipeAccess = isAdmin || isPaidUser;

  // Categories
  const categories = [
    { id: 'all', label: 'All Recipes', icon: '🍽️' },
    { id: 'soups_stews', label: 'Soups & Stews', icon: '🍲' },
    { id: 'meat_fish', label: 'Meat & Fish', icon: '🐟' },
    { id: 'vegetables', label: 'Vegetables', icon: '🥬' },
    { id: 'grains', label: 'Grains', icon: '🌾' },
    { id: 'bread_cakes', label: 'Bread & Cakes', icon: '🍞' },
    { id: 'beverages', label: 'Beverages', icon: '🍵' },
    { id: 'desserts', label: 'Desserts', icon: '🍰' }
  ];

  // Load recipes on mount
  useEffect(() => {
    loadRecipes();
  }, []);

  // Filter recipes when search or category changes
  useEffect(() => {
    filterRecipes();
  }, [searchTerm, selectedCategory, recipes]);

  const loadRecipes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/tribal-recipes/search?limit=100`);
      if (!response.ok) throw new Error('Failed to load recipes');
      
      const data = await response.json();
      setRecipes(data);
      setFilteredRecipes(data);
    } catch (error) {
      console.error('Error loading recipes:', error);
      toast({
        title: "Error",
        description: "Failed to load recipes. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Show subscription gate if user doesn't have access
  if (!loading && !hasRecipeAccess) {
    return (
      <SubscriptionGate 
        feature="tribal_recipes_access"
        title="Unlock Sacred Tribal Recipes"
        description="Access 68+ traditional recipes with detailed cultural significance, ingredients, and preparation methods. Upgrade to Seekers Path or Crown membership to explore ancestral culinary wisdom."
      />
    );
  }

  const filterRecipes = () => {
    let filtered = [...recipes];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(r => r.category === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(r => 
        r.recipe_name?.toLowerCase().includes(search) ||
        r.name?.toLowerCase().includes(search) ||
        r.tribe_name?.toLowerCase().includes(search) ||
        r.tribe?.toLowerCase().includes(search) ||
        r.ingredients?.some(ing => 
          (typeof ing === 'string' ? ing : ing.name)?.toLowerCase().includes(search)
        )
      );
    }

    setFilteredRecipes(filtered);
  };

  const openRecipeDetail = async (recipeId) => {
    try {
      const response = await fetch(`${API_URL}/api/tribal-recipes/${recipeId}`);
      if (!response.ok) throw new Error('Failed to load recipe details');
      
      const data = await response.json();
      setSelectedRecipe(data);
    } catch (error) {
      console.error('Error loading recipe details:', error);
      toast({
        title: "Error",
        description: "Failed to load recipe details.",
        variant: "destructive"
      });
    }
  };

  const closeRecipeDetail = () => {
    setSelectedRecipe(null);
  };

  // Close modal on ESC key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && selectedRecipe) {
        closeRecipeDetail();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [selectedRecipe]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sacred recipes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4 flex items-center justify-center gap-3">
            <ChefHat className="w-12 h-12 text-green-600" />
            Tribal Table
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Sacred recipes, ancestral wisdom, and traditional cooking methods from Indigenous communities
          </p>
          <div className="mt-6 flex justify-center gap-8 text-sm">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <span className="font-semibold">{recipes.length} Traditional Recipes</span>
            </div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="font-semibold">Cultural Heritage</span>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <Card className="mb-8 shadow-lg">
          <CardContent className="p-6">
            {/* Search Bar */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search recipes, tribes, or ingredients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 py-6 text-lg"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-3">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-2 rounded-full font-medium transition-all ${
                    selectedCategory === cat.id
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-green-50 border border-gray-200'
                  }`}
                >
                  <span className="mr-2">{cat.icon}</span>
                  {cat.label}
                </button>
              ))}
            </div>

            <div className="mt-4 text-sm text-gray-600">
              Showing {filteredRecipes.length} of {recipes.length} recipes
            </div>
          </CardContent>
        </Card>

        {/* Recipe Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map((recipe) => (
            <Card 
              key={recipe.id}
              className="hover:shadow-xl transition-shadow cursor-pointer group overflow-hidden"
              onClick={() => openRecipeDetail(recipe.id)}
            >
              {/* Recipe Image */}
              <div className="relative h-48 overflow-hidden bg-gray-200">
                {recipe.image_url ? (
                  <img
                    src={recipe.image_url}
                    alt={recipe.recipe_name || recipe.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-green-100 to-emerald-200">
                    <ChefHat className="w-16 h-16 text-green-600 opacity-50" />
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-semibold">
                  {recipe.category?.replace('_', ' ').toUpperCase()}
                </div>
              </div>

              {/* Recipe Info */}
              <CardContent className="p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                  {recipe.recipe_name || recipe.name}
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  {recipe.tribe_name || recipe.tribe || 'Indigenous Community'}
                </p>
                
                {/* Quick Stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>
                      {(() => {
                        const prepTime = recipe.prep_time_minutes || 0;
                        const cookTime = recipe.cook_time_minutes || 0;
                        const totalTime = prepTime + cookTime;
                        return totalTime > 0 ? `${totalTime} min` : recipe.prep_time || 'Unknown';
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>
                      {(() => {
                        const servings = recipe.servings;
                        if (typeof servings === 'number') {
                          return `${servings} servings`;
                        }
                        return recipe.serves || `${servings || 6} servings`;
                      })()}
                    </span>
                  </div>
                </div>

                <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                  View Recipe
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredRecipes.length === 0 && (
          <div className="text-center py-16">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No recipes found</h3>
            <p className="text-gray-500">Try adjusting your search or filters</p>
          </div>
        )}
      </div>

      {/* Recipe Detail Modal */}
      {selectedRecipe && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={closeRecipeDetail}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full my-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Image */}
            <div className="relative h-64 rounded-t-2xl overflow-hidden bg-gradient-to-br from-green-200 to-emerald-300">
              {selectedRecipe.image_url ? (
                <img
                  src={selectedRecipe.image_url}
                  alt={selectedRecipe.recipe_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ChefHat className="w-24 h-24 text-green-600 opacity-50" />
                </div>
              )}
              {/* Close Button - Top Right */}
              <button
                onClick={closeRecipeDetail}
                className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-full hover:bg-white transition-all hover:scale-110 shadow-lg group"
                aria-label="Close recipe"
              >
                <X className="w-6 h-6 text-gray-700 group-hover:text-red-600 transition-colors" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8 max-h-[600px] overflow-y-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-2">{selectedRecipe.recipe_name || selectedRecipe.name}</h2>
              <p className="text-lg text-gray-600 mb-6">{selectedRecipe.tribe_name || selectedRecipe.tribe || 'Indigenous Community'}</p>

              {/* Recipe Meta */}
              <div className="grid grid-cols-3 gap-4 mb-8 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <Clock className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <div className="text-sm text-gray-600">Total Time</div>
                  <div className="font-semibold">
                    {(() => {
                      const prep = selectedRecipe.prep_time_minutes || 0;
                      const cook = selectedRecipe.cook_time_minutes || 0;
                      const total = prep + cook;
                      return total > 0 ? `${total} min` : selectedRecipe.prep_time || 'Unknown';
                    })()}
                  </div>
                </div>
                <div className="text-center">
                  <Users className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <div className="text-sm text-gray-600">Servings</div>
                  <div className="font-semibold">
                    {selectedRecipe.servings || selectedRecipe.serves || 6}
                  </div>
                </div>
                <div className="text-center">
                  <ChefHat className="w-6 h-6 mx-auto mb-2 text-green-600" />
                  <div className="text-sm text-gray-600">Difficulty</div>
                  <div className="font-semibold capitalize">
                    {selectedRecipe.difficulty || 'Medium'}
                  </div>
                </div>
              </div>

              {/* Cultural Significance - Focusing on Tribal Traditions */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                  Cultural Significance
                </h3>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-6 rounded-lg">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedRecipe.enhanced_cultural_significance || selectedRecipe.cultural_significance || 'This traditional recipe carries important cultural heritage and ancestral knowledge passed down through generations.'}
                  </p>
                </div>
              </div>

              {/* Ingredients List with Measurements */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Ingredients with Measurements</h3>
                {selectedRecipe.ingredients && selectedRecipe.ingredients.length > 0 ? (
                  <div className="space-y-2">
                    {selectedRecipe.ingredients.map((ingredient, index) => {
                      const name = typeof ingredient === 'object' ? ingredient.name : ingredient;
                      const amount = typeof ingredient === 'object' ? ingredient.amount : '';
                      const notes = typeof ingredient === 'object' ? ingredient.notes : '';
                      
                      return (
                        <div key={index} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex-1">
                            <div className="flex items-baseline gap-3">
                              <span className="font-semibold text-gray-900">{name}</span>
                              {amount && <span className="font-bold text-green-700">{amount}</span>}
                            </div>
                            {notes && <p className="text-sm text-gray-600 mt-1 italic">{notes}</p>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg text-center">
                    <p className="text-gray-600">Ingredients list is being compiled for this recipe.</p>
                  </div>
                )}
              </div>

              {/* Preparation Steps */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-blue-500" />
                  Preparation Steps
                </h3>
                {selectedRecipe.enhanced_preparation_steps && selectedRecipe.enhanced_preparation_steps.length > 0 ? (
                  <div className="space-y-4">
                    {selectedRecipe.enhanced_preparation_steps.map((step, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                          <p className="text-gray-700 leading-relaxed">{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : selectedRecipe.instructions && selectedRecipe.instructions.length > 0 ? (
                  <div className="space-y-4">
                    {selectedRecipe.instructions.map((step, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                          {index + 1}
                        </div>
                        <div className="flex-1 bg-blue-50 p-4 rounded-lg">
                          <p className="text-gray-700 leading-relaxed">{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <p className="text-gray-600">Preparation steps are being compiled for this recipe.</p>
                  </div>
                )}
              </div>

              {/* Close Button at Bottom */}
              <div className="mt-8 pt-6 border-t border-gray-200 flex justify-center gap-4">
                <Button
                  onClick={closeRecipeDetail}
                  className="px-8 py-3 bg-gray-600 hover:bg-gray-700 text-white text-lg"
                >
                  Close Recipe
                </Button>
                <button
                  onClick={closeRecipeDetail}
                  className="px-8 py-3 border-2 border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-lg font-medium"
                >
                  Back to Recipes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TribalTable;
